import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpBackend, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslocoRootModule } from 'src/app/transloco-root.module';
import { InvoiceService } from 'src/app/service/invoice.service';
import { Dps, DpsParte, parseDps } from 'src/app/shared/nfse/dps-parser';
import { saveNfseXml } from 'src/app/shared/nfse/nfse-download.util';

/** A DPS só traz o CNPJ do prestador; nome e endereço da FuelTech vêm daqui. */
const FUELTECH = {
  cnpj: '05704744000100',
  nome: 'Fueltech',
  endereco: 'Avenida das Indústrias, 864 - Anchieta, Porto Alegre - RS, CEP 90200-290',
};

const IBGE_MUNICIPIOS = 'https://servicodados.ibge.gov.br/api/v1/localidades/municipios';

// Tabelas de códigos do leiaute nacional da DPS (v1.01). Código fora da tabela aparece cru.
const SIMPLES_NACIONAL: Record<string, string> = {
  '1': 'Não optante',
  '2': 'Optante - MEI',
  '3': 'Optante - ME/EPP',
};
const REGIME_ESPECIAL: Record<string, string> = {
  '0': 'Nenhum',
  '1': 'Ato cooperado',
  '2': 'Estimativa',
  '3': 'Microempresa municipal',
  '4': 'Notário ou registrador',
  '5': 'Profissional autônomo',
  '6': 'Sociedade de profissionais',
};
const TRIBUTACAO_ISSQN: Record<string, string> = {
  '1': 'Operação tributável',
  '2': 'Imunidade',
  '3': 'Exportação de serviço',
  '4': 'Não incidência',
};
const RETENCAO_ISSQN: Record<string, string> = {
  '1': 'Não retido',
  '2': 'Retido pelo tomador',
  '3': 'Retido pelo intermediário',
};
const RETENCAO_PIS_COFINS: Record<string, string> = {
  '0': 'Não retidos',
};

type LoadError = 'notAvailable' | 'loadError' | 'invalid';

/**
 * Representação imprimível da DPS da fatura (GET /user/invoices/{id}/nf-xml). Não é o DANFSe:
 * a DPS não tem número da NFS-e, chave de acesso nem QR code, e a página avisa isso.
 */
@Component({
  selector: 'app-nfse-document',
  standalone: true,
  imports: [CommonModule, ButtonModule, ProgressSpinnerModule, TranslocoRootModule],
  templateUrl: './nfse-document.component.html',
  styleUrl: './nfse-document.component.css',
})
export class NfseDocumentComponent implements OnInit {
  invoiceId = '';
  dps: Dps | null = null;
  loading = true;
  error: LoadError | null = null;

  private xml: Blob | null = null;
  private municipios: Record<string, string> = {};
  /** Sem interceptors: a URL do IBGE contém /api/ e levaria o token do Cognito junto. */
  private publicHttp: HttpClient;

  constructor(
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    httpBackend: HttpBackend,
  ) {
    this.publicHttp = new HttpClient(httpBackend);
  }

  ngOnInit(): void {
    this.invoiceId = this.route.snapshot.paramMap.get('id') ?? '';
    this.invoiceService.downloadNfXml(this.invoiceId).subscribe({
      next: async (blob) => {
        this.xml = blob;
        try {
          this.dps = parseDps(await blob.text());
          this.loadMunicipios(this.dps);
        } catch {
          this.error = 'invalid';
        }
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.error = err.status === 404 ? 'notAvailable' : 'loadError';
        this.loading = false;
      },
    });
  }

  print(): void {
    window.print();
  }

  downloadXml(): void {
    if (this.xml) saveNfseXml(this.xml, this.invoiceId);
  }

  isFueltech(parte: DpsParte | null): boolean {
    return parte?.documento === FUELTECH.cnpj;
  }

  nomePrestador(parte: DpsParte | null): string | null {
    return parte?.nome ?? (this.isFueltech(parte) ? FUELTECH.nome : null);
  }

  enderecoPrestador(parte: DpsParte | null): string | null {
    return parte?.endereco ? this.endereco(parte) : this.isFueltech(parte) ? FUELTECH.endereco : null;
  }

  endereco(parte: DpsParte | null): string | null {
    const end = parte?.endereco;
    if (!end) return null;
    const rua = [end.logradouro, end.numero].filter(Boolean).join(', ');
    return [rua, end.complemento, end.bairro, this.municipio(end.codigoMunicipio), end.cep && `CEP ${this.cep(end.cep)}`]
      .filter(Boolean)
      .join(' - ');
  }

  /** Nome e UF pelo IBGE; enquanto não chega (ou se falhar), mostra o código. */
  municipio(codigo: string | null): string {
    if (!codigo) return '';
    return this.municipios[codigo] ?? `Município ${codigo}`;
  }

  documento(valor: string | null): string {
    if (!valor) return '';
    if (valor.length === 14) return valor.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (valor.length === 11) return valor.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return valor;
  }

  cep(valor: string): string {
    return valor.replace(/^(\d{5})(\d{3})$/, '$1-$2');
  }

  fone(valor: string | null): string {
    if (!valor) return '';
    const digitos = valor.replace(/^0+/, '');
    if (digitos.length === 10) return digitos.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    if (digitos.length === 11) return digitos.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
    return valor;
  }

  /** dhEmi já vem com o fuso do emissor; exibe no horário de Brasília. */
  dataHora(valor: string | null): string {
    if (!valor) return '';
    return new Date(valor).toLocaleString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      dateStyle: 'short',
      timeStyle: 'short',
    });
  }

  /** dCompet é só data (AAAA-MM-DD); formata sem passar por Date para não mudar de dia com o fuso. */
  data(valor: string | null): string {
    return valor ? valor.split('-').reverse().join('/') : '';
  }

  moeda(valor: number | null): string {
    return valor === null ? '' : valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  percentual(valor: number | null): string {
    return valor === null ? '' : `${valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`;
  }

  /** cTribNac 030201 -> 03.02.01 */
  codigoTributacao(valor: string | null): string {
    return valor?.replace(/^(\d{2})(\d{2})(\d{2})$/, '$1.$2.$3') ?? '';
  }

  /** cNBS 111042000 -> 1.1104.20.00 */
  codigoNbs(valor: string | null): string {
    return valor?.replace(/^(\d)(\d{4})(\d{2})(\d{2})$/, '$1.$2.$3.$4') ?? '';
  }

  simplesNacional(codigo: string | null): string {
    return this.descricao(SIMPLES_NACIONAL, codigo);
  }

  regimeEspecial(codigo: string | null): string {
    return this.descricao(REGIME_ESPECIAL, codigo);
  }

  tributacaoIssqn(codigo: string | null): string {
    return this.descricao(TRIBUTACAO_ISSQN, codigo);
  }

  retencaoIssqn(codigo: string | null): string {
    return this.descricao(RETENCAO_ISSQN, codigo);
  }

  retencaoPisCofins(codigo: string | null): string {
    return this.descricao(RETENCAO_PIS_COFINS, codigo);
  }

  private descricao(tabela: Record<string, string>, codigo: string | null): string {
    if (!codigo) return '';
    return tabela[codigo] ?? `Código ${codigo}`;
  }

  private loadMunicipios(dps: Dps): void {
    const codigos = new Set(
      [
        dps.codigoMunicipioEmissao,
        dps.servico.codigoMunicipioPrestacao,
        dps.prestador?.endereco?.codigoMunicipio,
        dps.tomador?.endereco?.codigoMunicipio,
      ].filter((c): c is string => !!c),
    );
    codigos.forEach((codigo) =>
      this.publicHttp.get<any>(`${IBGE_MUNICIPIOS}/${codigo}`).subscribe({
        next: (m) => {
          const uf = m?.['regiao-imediata']?.['regiao-intermediaria']?.UF?.sigla ?? m?.microrregiao?.mesorregiao?.UF?.sigla;
          if (m?.nome) this.municipios[codigo] = uf ? `${m.nome}/${uf}` : m.nome;
        },
        // Sem o IBGE a página continua com o código do município.
        error: () => {},
      }),
    );
  }
}
